/**
 * TanStack Query (React Query) Configuration
 * 
 * This file configures the global QueryClient with sensible defaults
 * for the MotoTrips application.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,

      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,

      // Smart retry: cover cold-start / migration warm-up windows (up to ~55s total)
      // - Network errors & 5xx → retry up to 8 times with exponential backoff
      // - 4xx client errors → no retry (permanent failures like 404)
      retry: (failureCount, error) => {
        const status = error?.response?.status ?? error?.status;
        if (!status || status === 0 || status >= 500) {
          return failureCount < 8;
        }
        return false;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),

      // Don't refetch on window focus by default (can override per-query)
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Query key factory for consistent key management
 * 
 * Usage:
 * - queryKeys.trips.all() => ['trips']
 * - queryKeys.trips.list(filters) => ['trips', 'list', filters]
 * - queryKeys.trips.detail(id) => ['trips', 'detail', id]
 */
export const queryKeys = {
  // Trips
  trips: {
    all: () => ['trips'],
    lists: () => [...queryKeys.trips.all(), 'list'],
    list: (filters) => [...queryKeys.trips.lists(), filters],
    public: () => [...queryKeys.trips.all(), 'public'],
    details: () => [...queryKeys.trips.all(), 'detail'],
    detail: (id) => [...queryKeys.trips.details(), id],
    pinned: () => [...queryKeys.trips.all(), 'pinned'],
    pinnedIds: () => [...queryKeys.trips.pinned(), 'ids'],
    liked: () => [...queryKeys.trips.all(), 'liked'],
    likedIds: () => [...queryKeys.trips.liked(), 'ids'],
    myTrips: () => [...queryKeys.trips.all(), 'my'],
  },
  
  // Clubs
  clubs: {
    all: () => ['clubs'],
    lists: () => [...queryKeys.clubs.all(), 'list'],
    list: (filters) => [...queryKeys.clubs.lists(), filters],
    public: () => [...queryKeys.clubs.all(), 'public'],
    details: () => [...queryKeys.clubs.all(), 'detail'],
    detail: (id) => [...queryKeys.clubs.details(), id],
    members: (id) => [...queryKeys.clubs.detail(id), 'members'],
  },
  
  // Users
  users: {
    all: () => ['users'],
    current: () => [...queryKeys.users.all(), 'me'],
    profile: (id) => [...queryKeys.users.all(), 'profile', id],
    public: (id) => [...queryKeys.users.all(), 'public', id],
  },
  
  // Settings
  settings: {
    all: () => ['settings'],
    config: () => [...queryKeys.settings.all(), 'config'],
    sliders: () => [...queryKeys.settings.all(), 'sliders'],
    siteAccess: () => [...queryKeys.settings.all(), 'siteAccess'],
    registration: () => [...queryKeys.settings.all(), 'registration'],
    security: () => [...queryKeys.settings.all(), 'security'],
    export: () => [...queryKeys.settings.all(), 'export'],
  },
  
  // Notifications
  notifications: {
    all: () => ['notifications'],
    unread: () => [...queryKeys.notifications.all(), 'unread'],
    list: (page) => [...queryKeys.notifications.all(), 'list', page],
  },
  
  // Motorcycles
  motorcycles: {
    all: () => ['motorcycles'],
    myGarage: () => [...queryKeys.motorcycles.all(), 'my'],
    catalog: () => [...queryKeys.motorcycles.all(), 'catalog'],
    brands: () => [...queryKeys.motorcycles.catalog(), 'brands'],
    models: (brand) => [...queryKeys.motorcycles.catalog(), 'models', brand],
  },
};
